/**
 * @fullvsl/app-shell/views/analytics — analytics view (FUL-225/244).
 *
 * Embeds the real dashboard renderer (`createDashboard`). When the backend API
 * is live (served by the FullVSL HTTP server), it uses `HttpAnalyticsSource`
 * for real data; otherwise it falls back to `FixtureAnalyticsSource`.
 * Uses the same visual language as the portfolio dashboard while preserving
 * the detailed retention, funnel, breakdown, resume, and event surfaces.
 */
import type { VslStore } from "../store";
import { FixtureAnalyticsSource, HttpAnalyticsSource, type AnalyticsDataSource } from "../../../dashboard/src/analytics-client";
import { createDashboard } from "../../../dashboard/src/dashboard";
import { el, append } from "../dom";

export function renderAnalyticsView(
  store: VslStore,
  projectId: string,
  navigate: (path: string) => void,
): { mount: HTMLElement; cleanup: () => void } {
  const project = store.get(projectId);
  const wrapper = el("div");

  if (!project) {
    const empty = el("div", "fva-empty");
    append(empty, el("h3", null, "VSL não encontrada"));
    append(wrapper, empty);
    return { mount: wrapper, cleanup: () => {} };
  }

  // Publicada source when the store hydrated from the backend; fixture otherwise.
  const source: AnalyticsDataSource = store.live
    ? new HttpAnalyticsSource(window.location.origin)
    : new FixtureAnalyticsSource();

  const toolbar = el("div", "fva-an-detail-toolbar");
  const back = el("button", "fva-btn fva-btn-secondary", "← Todas as estatísticas") as HTMLButtonElement;
  back.addEventListener("click", () => navigate("#/analytics"));
  const actions = el("div", "fva-an-detail-actions");
  const edit = el("button", "fva-btn fva-btn-secondary", "Editar VSL") as HTMLButtonElement;
  edit.addEventListener("click", () => navigate(`#/vsl/${project.id}`));
  const preview = el("button", "fva-btn fva-btn-primary", "Visualizar player") as HTMLButtonElement;
  preview.addEventListener("click", () => navigate(`#/vsl/${project.id}/preview`));
  append(actions, edit, preview);
  append(toolbar, back, actions);
  append(wrapper, toolbar);

  // Dashboard host
  const dashWrap = el("div", "fva-analytics-wrap");
  const dashHost = el("div");
  dashWrap.appendChild(dashHost);
  append(wrapper, dashWrap);

  let cancelled = false;
  createDashboard(dashHost, { source, vslId: project.id, vslName: project.name }).catch((err) => {
    if (cancelled) return;
    dashHost.innerHTML = `<div class="fva-error-banner">Não foi possível carregar o painel: ${
      err instanceof Error ? err.message : String(err)
    }</div>`;
  });

  return {
    mount: wrapper,
    cleanup: () => {
      cancelled = true;
    },
  };
}
